<template>
  <div class="d-flex flex-column page">
    <!--Header-->
    <div class="page-header flex-shrink-0 border-bottom bg-white" v-show="true">
      <div class="container-fluid px-md-4 px-3">
        <div class="row no-gutters align-items-center justify-content-between">
          <div class="col-md-3 col order-md-1 order-1">
            <h3 class="mb-0">Tasks</h3>
          </div>
          <div
            class="col-md-auto col-12 d-flex justify-content-md-center justify-content-start order-md-2 order-3 mt-md-0 mt-2">
            <div class="w-100">
              <b-button-group class="btn-group-filter w-100">
                <b-button class="btn-light px-3" :class="{ 'active': (currentView === 1) }"
                  @click="currentView = 1">List
                  View</b-button>
                <b-button class="btn-light px-3" :class="{ 'active': (currentView === 2) }"
                  @click="currentView = 2">Current
                  Tasks</b-button>
              </b-button-group>
            </div>
          </div>
          <div class="col-md-3 col-auto d-flex justify-content-end order-md-3 order-2">
            <b-btn variant="success" @click="openTaskModal(null)" :disabled="!selectedPropertyId">
              <i class="fas fa-calendar-plus mr-1"></i>Add <span class="d-md-inline d-none">Task</span>
            </b-btn>
          </div>
        </div>
      </div>
    </div>

    <!--Search-->
    <div class="container-fluid px-md-4 px-3 py-3 bg-white border-bottom mb-3">
      <div class="row no-gutters">
        <div class="col-lg-4 col-md-6 col-12 pr-3">
          <div>
            <b-form-select v-model="selectedPropertyId" :options="properties" text-field="name" value-field="id"
              @change="onSelectProperty">
              <!-- This slot appears above the options from 'options' prop -->
              <template #first>
                <b-form-select-option :value="null" disabled>-- Choose a property to continue
                  --</b-form-select-option>
              </template>
            </b-form-select>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-end mt-md-0 mt-2">
          <div class="view-week-picker" v-if="currentView === 2">
            <b-input-group class="flex-nowrap">
              <b-input-group-prepend>
                <b-button variant="" @click="onSelectPreviousWeek"><i class="fas fa-caret-left"></i></b-button>
              </b-input-group-prepend>
              <date-picker :open.sync="datePickerOpen" @input="onSelectDate" v-model="weekOf"
                format="Week of MM/DD/YYYY" :formatter="momentFormat" :clearable="false" :editable="false"
                input-class="form-control bg-white">
                <template v-slot:footer="{ emit }">
                  <button class="mx-btn mx-btn-text" @click="onSelectToday(emit);">This Week</button>
                </template>
              </date-picker>
              <b-input-group-append>
                <b-button variant="" @click="onSelectNextWeek"><i class="fas fa-caret-right"></i></b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
      </div>
    </div>

    <!--Progress Indicator-->
    <div v-if="loading && selectedPropertyId" class="form-container p-5 p-md-4 d-flex justify-content-center h-100">
      <span><b-spinner small label="Small Spinner"></b-spinner> Loading Routine Tasks...</span>
    </div>

    <!--Table-->
    <div v-else class="container-fluid flex-grow-1 overflow-auto px-md-4 px-3">
      <div class="alert alert-primary" v-if="!selectedPropertyId">Choose a property to continue</div>
      <div v-if="tasks && tasks.length > 0">
        <!-- Routine Task Instance -->
        <routine-task-instances @edit-task="openTaskModal" @view-task-instance="goToInstance" v-if="currentView===2"
          class="" :propertyId="selectedPropertyId" :startDate="startDate" :endDate="endDate"
          :key="taskInstanceRenderKey"></routine-task-instances>
        <div v-if="currentView===1">
          <!--Table-->
          <b-table-simple bordered class="rg-table mb-5">
            <b-thead>
              <b-tr>
                <b-th>Name</b-th>
                <b-th>Start Date</b-th>
                <b-th class="text-center">Actions</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="task in tasks" v-bind:key="task.id">
                <b-td class="emphasis">
                  <div>{{ task.name }}</div>
                </b-td>
                <b-td>
                  <div>{{ task.startDateLocal | formatDateNoTime }}</div>
                </b-td>
                <b-td class="table-action text-center">
                  <b-btn title="Edit Procedure" @click="openTaskModal(task)" variant="link">
                    <i class="fas fa-edit"></i>
                  </b-btn>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>
      </div>
      <div v-if="tasks && tasks.length == 0 && selectedPropertyId != null"
        class="form-container p-5 p-md-4 d-flex justify-content-center align-items-center"
        style="height:calc(100% - 30px)">
        <i class="text-muted">No tasks found</i>
      </div>
    </div>

    <!-- Add Edit Modal -->
    <ValidationObserver v-slot="{ invalid }" ref="routineTaskValidationObserver">
      <b-modal size="lg" v-model="taskModalOpen" title="Task / Inspection" no-close-on-backdrop no-close-on-esc
        hide-header-close scrollable>
        <div v-if="taskToSave" class="px-md-4 pt-md-3">
          <b-form v-on:submit.prevent>
            <b-form-group>
              <label>Task / Inspection</label>
              <div v-if="procedureOptions">
                <ValidationProvider name="Procedure" rules="required" v-slot="validation" immediate>
                  <b-form-select :options="procedureOptions" v-model="taskToSave.procedureId"
                    :state="spr2_getValidationState(validation)">
                  </b-form-select>
                </ValidationProvider>
                <input type="hidden" v-model="taskToSave.flavor" value="RoutineTask" />
              </div>
              <div v-else>No procedures found</div>
            </b-form-group>
            <b-form-group>
              <label>Task Name</label>
              <ValidationProvider name="Name" rules="required" v-slot="validation" immediate>
                <b-form-input placeholder="Name" v-model="taskToSave.name"
                  :state="spr2_getValidationState(validation)" />
                <b-form-invalid-feedback id="name-feedback">
                  {{ validation.errors[0] }}
                </b-form-invalid-feedback>
              </ValidationProvider>
            </b-form-group>
            <!--
              <b-form-group>
                <b-form-checkbox class="mr-3" v-model="taskToSave.performAdHoc" switch><span>Perform Ad hoc?</span></b-form-checkbox>
              </b-form-group>
              -->
            <div class="row no-gutters mt-5">
              <div class="col-12">
                <label class="section-label"><i class="fas fa-calendar-o" aria-hidden="true"></i> SCHEDULE</label>
              </div>
            </div>
            <b-form-group>
              <div class="d-flex flex-wrap">
                <!-- Schedule Type -->
                <div class="mr-2" style="max-width:180px">
                  <label>Occurs</label>
                  <div>
                    <ValidationProvider name="ScheduleType" rules="required" v-slot="validation" immediate>
                      <b-form-select v-model="taskToSave.schedule.routineTaskScheduleType" :options="occurrenceOptions"
                        :state="spr2_getValidationState(validation)" @change="onSelectScheduleType">
                        <!-- This slot appears above the options from 'options' prop -->
                        <template #first>
                          <b-form-select-option :value="null" disabled>-- Choose a schedule --</b-form-select-option>
                        </template>
                      </b-form-select>
                    </ValidationProvider>
                  </div>
                </div>
                <!-- Start Date -->
                <div class="mr-2" v-if="taskToSave.schedule.routineTaskScheduleType !== null">
                  <!--<b-form-datepicker v-model="taskToSave.startDateLocal" locale="en" placeholder="Choose a start date"></b-form-datepicker>-->
                  <label>{{taskToSave.schedule.routineTaskScheduleType === 'AdHoc'?'On':'Starting on'}}</label>
                  <div>
                    <ValidationProvider name="StartDate" rules="required" v-slot="validation" immediate>
                      <b-form-input class="form-control" type="date" v-model="taskToSave.startDateLocal"
                        name="startDate" id="startDate" :state="spr2_getValidationState(validation)"></b-form-input>
                    </ValidationProvider>
                  </div>
                </div>
                <!-- Time of day -->
                <div v-if="taskToSave.schedule.routineTaskScheduleType === 'Daily'">
                  <label>At</label>
                  <div>
                    <ValidationProvider name="DailyTime" rules="required" v-slot="validation" immediate>
                      <b-form-input type="time" class="form-control" value="00:00"
                        v-model="taskToSave.schedule.timeOfDay"
                        :state="spr2_getValidationState(validation)"></b-form-input>
                    </ValidationProvider>
                  </div>
                </div>
              </div>
              <div class="d-flex bg-light border mt-2 p-3"
                v-if="taskToSave.schedule.routineTaskScheduleType && taskToSave.schedule.routineTaskScheduleType !== 'AdHoc'">
                <!-- Quarterly -->
                <div class="ml-1" v-if="taskToSave.schedule.routineTaskScheduleType === 'Quarterly'"
                  style="max-width:380px">
                  <label>In Quarter(s)</label>
                  <ValidationProvider name="QuarterlyMonths" rules="required" v-slot="validation" immediate>
                    <div class="d-flex">
                      <div class="mr-2" v-for="(quarter, q_index) in quarterlyOptions" :key="q_index">
                        <label class="block text-uppercase">{{quarter.name}}</label>
                        <div v-for="option in quarter.months" :key="option">
                          <b-form-checkbox style="width: 90px" v-model="selectedMonthlyOptions" :key="option"
                            :value="option" :state="spr2_getValidationState(validation)">{{option}}
                          </b-form-checkbox>
                        </div>
                      </div>
                    </div>
                  </ValidationProvider>
                </div>
                <!-- Monthly -->
                <div class="ml-1" v-if="taskToSave.schedule.routineTaskScheduleType === 'Monthly'"
                  style="max-width:380px">
                  <label>In Month(s)</label>
                  <div>
                    <ValidationProvider name="MonthlyMonths" rules="required" v-slot="validation" immediate>
                      <b-form-checkbox style="width: 100px" v-model="selectedMonthlyOptions"
                        v-for="option in monthlyOptions" :key="option" :value="option"
                        :state="spr2_getValidationState(validation)" inline>{{option}}
                      </b-form-checkbox>
                    </ValidationProvider>
                  </div>
                </div>
                <!-- Weekly -->
                <div class="mr-2 ml-1" v-if="taskToSave.schedule.routineTaskScheduleType === 'Weekly'"
                  style="max-width:415px">
                  <label>On Day(s)</label>
                  <div>
                    <ValidationProvider name="WeeklyDays" rules="required" v-slot="validation" immediate>
                      <b-form-checkbox style="width: 90px" v-model="selectedWeeklyOptions"
                        v-for="option in weeklyOptions" :key="option" :value="option"
                        :state="spr2_getValidationState(validation)" inline>{{option}}
                      </b-form-checkbox>
                    </ValidationProvider>
                  </div>
                </div>
                <!-- Daily -->
                <div class="mr-2" v-if="taskToSave.schedule.routineTaskScheduleType === 'Daily'">
                  <label>Every {{taskToSave.schedule.everyXDays}} day(s)</label>
                  <div>
                    <ValidationProvider name="EveryXDays" rules="min_value:1" v-slot="validation" immediate>
                      <b-form-input type="number" min="1" max="28" class="form-control"
                        v-model="taskToSave.schedule.everyXDays"
                        :state="spr2_getValidationState(validation)"></b-form-input>
                    </ValidationProvider>
                  </div>
                </div>
                <!-- Day of month -->
                <div class="ml-3 pl-3 border-left">
                  <div
                    v-if="taskToSave.schedule.routineTaskScheduleType === 'Monthly' || taskToSave.schedule.routineTaskScheduleType === 'Quarterly'">
                    <label>On day</label>
                    <div class="mb-1">
                      <ValidationProvider name="DaysInMonth" rules="required" v-slot="validation" immediate>
                        <b-form-select v-model="taskToSave.schedule.dayOfMonth" :options="daysInMonthOptions"
                          :state="spr2_getValidationState(validation)">
                          <!-- This slot appears above the options from 'options' prop -->
                          <template #first>
                            <b-form-select-option :value="null" disabled>-- Choose a day --</b-form-select-option>
                          </template>
                        </b-form-select>
                      </ValidationProvider>
                    </div>
                  </div>
                  <div
                    v-if="taskToSave.schedule.routineTaskScheduleType && taskToSave.schedule.routineTaskScheduleType !== 'AdHoc'">
                    <b-form-checkbox name="includeWeekends" class="mr-3" v-model="taskToSave.schedule.includeWeekends"
                      switch>
                      <span>Include weekends</span></b-form-checkbox>
                  </div>
                </div>
              </div>
            </b-form-group>
            <b-form-group>
              <label>Locations</label>
              <div class="d-flex flex-wrap aligns-items-stretch mb-3">
                <!-- Location Type -->
                <div class="mr-2">
                  <div>
                    <b-form-select v-model="tempLocationType" style="min-width: 160px;">
                      <!-- This slot appears above the options from 'options' prop -->
                      <template #first>
                        <b-form-select-option :value="'Adhoc'" disabled>Ad hoc</b-form-select-option>
                        <!--<b-form-select-option :value="NotAdHoc" disabled>-- Choose a schedule --</b-form-select-option>-->
                      </template>
                    </b-form-select>
                  </div>
                </div>
                <div class="flex-fill">
                  <div>
                    <!-- Location Name -->
                    <b-input-group>
                      <b-form-input :state="Boolean(taskToSave.locations.length)" autocomplete="off"
                        class="form-control" type="text" v-model="tempLocationName" name="locationName"
                        id="locationName"
                        placeholder="Enter a location name (e.g., Unit #1, Pool, etc.)"></b-form-input>
                      <b-input-group-append>
                        <b-button id='btnAddLocation' variant="success" @click="doAddLocation"
                          :disabled="!isLocationToAddValid"><i class="fas fa-plus"></i> </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                </div>
              </div>
              <div class="mb-2" v-if="taskToSave.locations.length > 0">
                <div class="bg-secondary text-white d-flex rounded-top">
                  <div class="pl-2" style="width: 160px">Type</div>
                  <div class="pl-2">Name</div>
                </div>
                <b-list-group>
                  <b-list-group-item class="pl-2" v-for="location in taskToSave.locations" :key="location.name">
                    <div class="d-flex">
                      <div style="width:160px">{{location.routineTaskLocationType | updateAdHocText}}</div>
                      <div class="flex-fill">{{location.name}}</div>
                      <div style="cursor: pointer;"><i class="fas fa-trash text-danger"
                          @click="doRemoveLocation(location)"></i></div>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </div>
            </b-form-group>
            <div class="row no-gutters mt-5">
              <div class="col-12">
                <label class="section-label"><i class="fas fa-user" aria-hidden="true"></i> RESPONSIBILITY</label>
              </div>
            </div>
            <b-form-group>
              <label>Assign</label>
              <b-input-group class="mb-3">
                <b-form-select :state="Boolean(taskToSave.assignedPeopleRealized.length)" class="form-controlx"
                  :options="filteredPeople" v-model="tempPersonId"></b-form-select>
                <b-input-group-append>
                  <b-button id='btnAddPerson' variant="success" @click="doAddPerson" :disabled="!tempPersonId"><i
                      class="fas fa-plus"></i> </b-button>
                </b-input-group-append>
              </b-input-group>
              <div class="mb-2" v-if="taskToSave.assignedPeopleRealized.length > 0">
                <div class="bg-secondary text-white d-flex rounded-top">
                  <div class="pl-2">Name</div>
                </div>
                <b-list-group>
                  <b-list-group-item class="pl-2" v-for="person in taskToSave.assignedPeopleRealized" :key="person.id">
                    <div class="d-flex">
                      <div class="flex-fill">{{person | fullName}}</div>
                      <div style="cursor: pointer;"><i class="fas fa-trash text-danger"
                          @click="doRemovePerson(person)"></i></div>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </div>
            </b-form-group>
            <b-form-group>
              <label>Assign Roles</label>
              <b-input-group class="mb-3">
                <b-form-select class="form-controlx" :options="filteredRoles" v-model="tempRoleId"></b-form-select>
                <b-input-group-append>
                  <b-button id='btnAddRole' variant="success" @click="doAddRole" :disabled="!tempRoleId"><i
                      class="fas fa-plus"></i> </b-button>
                </b-input-group-append>
              </b-input-group>
              <div class="mb-2" v-if="taskToSave.assignedRolesRealized.length > 0">
                <div class="bg-secondary text-white d-flex rounded-top">
                  <div class="pl-2">Role</div>
                </div>
                <b-list-group>
                  <b-list-group-item class="pl-2" v-for="role in taskToSave.assignedRolesRealized" :key="role.id">
                    <div class="d-flex">
                      <div class="flex-fill">{{role.name}}</div>
                      <div style="cursor: pointer;"><i class="fas fa-trash text-danger"
                          @click="doRemoveRole(role)"></i></div>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </div>
            </b-form-group>
            <!--{{taskToSave.schedule}}-->
            <!--{{taskToSave.locations}}-->
            <!--{{taskToSave.assignedPeople}}-->
          </b-form>
        </div>
        <template v-slot:modal-footer>
          <b-btn variant="light" @click="closeTaskModal()">Close</b-btn>
          <b-btn variant="primary" :disabled="invalid||emptyCollections()" @click="doSaveTask"
            class="float-right ml-1">Save</b-btn>
        </template>
      </b-modal>
    </ValidationObserver>
  </div>
</template>

<script>

import { mapActions } from 'vuex'
import { spr2Procedures, spr2Validation } from '../mixins/Spr2SharedFunctions'
import 'vue-datetime/dist/vue-datetime.css'
import moment from 'moment'
import RoutineTaskInstances from './RoutineTaskInstances.vue'

import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: 'Resources',
  mixins: [ spr2Procedures, spr2Validation ],
  components: {RoutineTaskInstances, DatePicker},
  data () {
    return {
      loading: false,
      currentView: 2, // 1 = Task View, 2 = Schedule View
      taskModalOpen: false,
      taskToSave: null,
      tasks: [],
      properties: [],
      selectedPropertyId: null,
      selectedProcedure: null,
      occurrenceOptions: ['AdHoc', /* 'Daily', */ 'Weekly', 'Monthly', 'Quarterly'],
      weeklyOptions: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      selectedWeeklyOptions: [],
      monthlyOptions: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      quarterlyOptions: [{name: 'q1', months: ['January', 'February', 'March']}, {name: 'q2', months: ['April', 'May', 'June']}, {name: 'q3', months: ['July', 'August', 'September']}, {name: 'q4', months: ['October', 'November', 'December']}],

      selectedMonthlyOptions: [],
      daysInMonthOptions: [],
      tempLocationType: 'Adhoc',
      tempLocationName: undefined,
      taskInstanceRenderKey: 0,
      tempPersonId: null,
      people: [],
      tempRoleId: null,
      roles: [],
      routineTaskProcedures: null,
      dateFormat: 'MM/DD/YYYY',
      weekOf: moment().startOf('week').toDate(),

      // Date picker specifics
      momentFormat: {
        stringify: (date) => {
          return date ? `Week of ${moment(date).format(this.dateFormat)}` : ''
        },
        parse: (value) => {
          return value ? moment(value, this.dateFormat).toDate() : null
        }
      },
      datePickerOpen: false
    }
  },
  async created () {
    this.loading = true
    for (var i = 1; i < 29; i++) {
      this.daysInMonthOptions.push(i)
    }
    await this.doGetRoutineTaskProcedures()
    await this.getLoggedInUserProperties()
      .then((result) => {
        console.log('setting event user properties: ' + JSON.stringify(result.data.properties, null, 2))
        this.properties = result.data.properties
        if (this.properties.length > 0) {
          // Restore selected property
          if (this.requestedPropertyId !== undefined) {
            this.selectedPropertyId = this.requestedPropertyId
          } else {
            this.selectedPropertyId = this.properties[0].id
          }
          // Restore selected date
          if (this.requestedWeekOf !== undefined) {
            this.weekOf = this.requestedWeekOf
          }
          this.onSelectProperty(this.selectedPropertyId)
        }
      })
    await this.doLoadPeople()
    await this.doLoadRoles()

    this.loading = false
  },
  methods: {
    ...mapActions('routineTasks', ['getRoutineTasks', 'saveRoutineTask']),
    ...mapActions('procedures', ['getRoutineTaskProcedures']),
    ...mapActions('events', ['getLoggedInUserProperties']),
    ...mapActions('company', ['getPeopleForManagement', 'getPositionTypes']),

    async doGetRoutineTasks () {
      await this.getRoutineTasks({propertyId: this.selectedPropertyId}).then((t) => {
        this.tasks = t.routineTasks
      })
      this.taskInstanceRenderKey += 1
    },
    goToInstance (instanceId) {
      this.$router.push({ name: 'RoutineTaskInstanceManage', params: { instanceId, fromPropertyId: this.selectedPropertyId, fromWeekOf: this.weekOf } })
    },
    async openTaskModal (task) {
      this.selectedWeeklyOptions = []
      // New Task
      if (task === null) {
        this.taskToSave = {
          startDateLocal: moment().format(this.dateFormat),
          procedureId: null,
          propertyId: this.selectedPropertyId,
          schedule: { routineTaskScheduleType: null, everyXDays: 7, dayOfMonth: null, includeWeekends: false, timeOfDay: '00:00' },
          locations: [],
          assignedPeopleRealized: [],
          assignedRolesRealized: []
        }
        this.taskModalOpen = true
      // Existing Task
      } else {
        this.taskModalOpen = true
        if (task.schedule == null) {
          task.schedule = {routineTaskScheduleType: null}
        }
        let date = task.startDateLocal == null ? null : moment(task.startDateLocal).format('YYYY-MM-DD')
        let time = task.schedule.timeOfDay === undefined ? '00:00' : moment(task.schedule.timeOfDay).format('HH:mm')
        let everyXDays = task.schedule.everyXDays == null ? 7 : task.schedule.everyXDays
        this.weeklyOptions.forEach((item) => {
          let day = task.schedule[item.toLowerCase()]
          if (day !== undefined && day === true) {
            this.selectedWeeklyOptions.push(item)
          }
        })

        this.monthlyOptions.forEach((item) => {
          let month = task.schedule[item.toLowerCase()]
          if (month !== undefined && month === true) {
            this.selectedMonthlyOptions.push(item)
          }
        })

        task.schedule.timeOfDay = time
        task.schedule.everyXDays = everyXDays

        this.taskToSave = {
          id: task.id,
          name: task.name,
          companyId: task.companyId,
          performAdHoc: task.performAdHoc,
          procedureId: task.procedureId,
          propertyId: task.propertyId,
          startDateLocal: date,
          schedule: task.schedule,
          locations: task.locations || [],
          assignedPeopleRealized: task.assignedPeopleRealized || [],
          assignedRolesRealized: task.assignedRolesRealized || []
        }
        await this.onSelectProcedure(task.procedureId)
      }
    },
    closeTaskModal () {
      this.taskModalOpen = false
    },
    async doSaveTask () {
      console.log(this.taskToSave)
      this.processing = true
      this.weeklyOptions.forEach((option) => {
        const found = this.selectedWeeklyOptions.find(selected => selected === option) !== undefined
        this.taskToSave.schedule[option] = found
      })
      this.monthlyOptions.forEach((option) => {
        const found = this.selectedMonthlyOptions.find(selected => selected === option) !== undefined
        this.taskToSave.schedule[option] = found
      })
      if (this.taskToSave.schedule.routineTaskScheduleType === 'AdHoc') {
        this.taskToSave.performAdHoc = true
      }
      
      // Build the unified assignees array
      this.taskToSave.assignees = []
      
      // Add people as assignees
      if (this.taskToSave.assignedPeopleRealized.length > 0) {
        const personAssignees = this.taskToSave.assignedPeopleRealized.map(p => ({
          assigneeId: p.id,
          assigneeType: 'Person'
        }))
        this.taskToSave.assignees.push(...personAssignees)
      }
      
      // Add roles as assignees
      if (this.taskToSave.assignedRolesRealized.length > 0) {
        const roleAssignees = this.taskToSave.assignedRolesRealized.map(r => ({
          assigneeId: r.id,
          assigneeType: 'Role'
        }))
        this.taskToSave.assignees.push(...roleAssignees)
      }
      
      await this.saveRoutineTask({task: this.taskToSave}).then((result) => {
        this.processing = false
        console.log(result)
        if (result.success) {
          this.taskModalOpen = false
          this.doGetRoutineTasks()
        } else {
          this.$toasted.error(result.failureReason, { duration: 4000 })
        }
      })
    },
    async doAddLocation () {
      if (this.tempLocationType !== undefined && this.tempLocationName !== undefined) {
        let location = {routineTaskLocationType: this.tempLocationType, name: this.tempLocationName}
        this.taskToSave.locations.push(location)
        this.tempLocationName = undefined
      }
      console.log(this.taskToSave)
      console.log(this.taskToSave.locations)
    },
    async doRemoveLocation (location) {
      const matchExpression = (item) => location.name === item.name && location.routineTaskLocationType === item.routineTaskLocationType
      let foundIndex = this.taskToSave.locations.findIndex(matchExpression)
      if (foundIndex > -1) {
        this.taskToSave.locations.splice(foundIndex, 1)
      }
    },
    async doLoadPeople () {
      await this.getPeopleForManagement(false).then(r => {
        this.people = r.data.people.map((p) => { return { text: `${p.firstName} ${p.lastName}`, value: p.id } })
        // Splice in a disabled default option
        this.people.splice(0, 0, {text: 'Select a Person to Assign', value: null, disabled: true, $isDisabled: true})
        /*
        // Or use this in the template
          <template #first>
            <b-form-select-option value="null">Select a Person to Notify</b-form-select-option>
          </template>
        */
      })
    },
    async doLoadRoles () {
      await this.getPositionTypes().then(response => {
        this.roles = response.data.types
          .filter(pt => pt.active)
          .map(pt => ({
            text: pt.name,
            value: pt.id
          }))
        // Splice in a disabled default option
        this.roles.splice(0, 0, {text: 'Select a Role to Assign', value: null, disabled: true, $isDisabled: true})
      })
    },
    async doAddPerson () {
      let p = this.people.find((item, index) => { return item.value === this.tempPersonId })

      let firstName = p.text.split(' ')[0]
      let lastName = p.text.split(' ')[1]
      let personToAdd = { id: p.value, firstName: firstName, lastName: lastName }
      this.taskToSave.assignedPeopleRealized.push(personToAdd)
      this.tempPersonId = null
    },
    async doRemovePerson (person) {
      const matchExpression = (item) => person.id === item.id
      let foundIndex = this.taskToSave.assignedPeopleRealized.findIndex(matchExpression)
      if (foundIndex > -1) {
        this.taskToSave.assignedPeopleRealized.splice(foundIndex, 1)
      }
    },
    async doAddRole () {
      if (!this.tempRoleId) return
      
      let r = this.roles.find((item) => { return item.value === this.tempRoleId })
      if (!r) return
      
      let roleToAdd = {
        id: r.value,
        name: r.text
      }
      this.taskToSave.assignedRolesRealized.push(roleToAdd)
      this.tempRoleId = null
    },
    async doRemoveRole (role) {
      const matchExpression = (item) => role.id === item.id
      let foundIndex = this.taskToSave.assignedRolesRealized.findIndex(matchExpression)
      if (foundIndex > -1) {
        this.taskToSave.assignedRolesRealized.splice(foundIndex, 1)
      }
    },
    async onSelectProperty (propertyId) {
      this.loading = true
      await this.doGetRoutineTasks(propertyId)
      this.loading = false
    },
    async onSelectProcedure (procedureId) {
      if (procedureId != null) {
        this.selectedProcedure = this.routineTaskProcedures.find((item) => { return item.value === procedureId })
      } else {
        this.selectedProcedure = null
      }
    },
    async onSelectScheduleType (scheduleType) {
      // Currently not needed
    },
    async doGetRoutineTaskProcedures () {
      this.getRoutineTaskProcedures().then((r) => { this.routineTaskProcedures = r.data })
    },
    onSelectNextWeek () {
      this.weekOf = moment(this.weekOf, this.dateFormat).add(1, 'week').toDate()
      this.taskInstanceRenderKey += 1
    },
    onSelectPreviousWeek () {
      this.weekOf = moment(this.weekOf, this.dateFormat).subtract(1, 'week').toDate()
      this.taskInstanceRenderKey += 1
    },
    onSelectDate (date) {
      this.weekOf = moment(date, this.dateFormat).startOf('week').toDate()
      this.taskInstanceRenderKey += 1
    },
    onSelectToday () {
      this.weekOf = moment().startOf('week').toDate()
      this.taskInstanceRenderKey += 1
      this.datePickerOpen = false
    },
    emptyCollections () {
      const hasPeople = this.taskToSave.assignedPeopleRealized && this.taskToSave.assignedPeopleRealized.length > 0
      const hasRoles = this.taskToSave.assignedRolesRealized && this.taskToSave.assignedRolesRealized.length > 0
      const hasAssignees = hasPeople || hasRoles
      
      return this.taskToSave.locations.length === 0 || !hasAssignees
    }
  },
  computed: {
    startDate () {
      return moment(this.weekOf, this.dateFormat).format(this.dateFormat)
    },
    endDate () {
      return moment(this.weekOf, this.dateFormat).endOf('week').format(this.dateFormat)
    },
    procedureOptions () {
      let procedures = this.routineTaskProcedures.map(x => {
        return {
          value: x.id,
          text: x.name
        }
      })
      procedures.splice(0, 0, {text: 'Select a Procedure', value: null, disabled: true})
      return procedures // .sort((a, b) => { return a.text < b.text ? -1 : (a.text > b.text) ? 1 : 0 })
    },
    isLocationToAddValid () {
      let isDefined = this.tempLocationType !== undefined && this.tempLocationName !== undefined && this.tempLocationName !== ''
      const matchExpression = (item) => isDefined && this.tempLocationName.toLowerCase() === item.name.toLowerCase() && this.tempLocationType.toLowerCase() === item.routineTaskLocationType.toLowerCase()
      let alreadyExists = this.taskToSave.locations.findIndex(matchExpression) > -1
      return isDefined && !alreadyExists
    },

    filteredPeople () {
      return this.people.filter((person) => {
        let alreadyExists = this.taskToSave.assignedPeopleRealized.findIndex(ap => ap.id === person.value) > -1
        return !alreadyExists
      })
    },
    filteredRoles () {
      return this.roles.filter((role) => {
        let alreadyExists = this.taskToSave.assignedRolesRealized.findIndex(ar => ar.id === role.value) > -1
        return !alreadyExists
      })
    },
    requestedPropertyId () {
      return this.$route.params.requestedPropertyId
    },
    requestedWeekOf () {
      return this.$route.params.requestedWeekOf
    }
  },
  filters: {
    updateAdHocText: function (value) {
      if (!value) return ''
      value = value.toString()
      return value.replace('Adhoc', 'Ad hoc')
    }
  }
}
</script>
<style scoped>
.mx-datepicker {
  width: 180px;
}
.view-week-picker .mx-datepicker .form-control[readonly] ,
.view-week-picker .input-group-prepend .btn,
.view-week-picker .input-group-append .btn{
  background-color: #fff;
  border: 1px solid #ced4da;
  color: black;
  cursor: pointer;
  margin: 0 -1px;
}
.mx-input-wrapper,.mx-input-wrapper .form-control[readonly] {
  cursor: pointer !important;
  background-color: red;
}
</style>



// WEBPACK FOOTER //
// src/views/RoutineTasks.vue